import { Edit, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { CustomLabelList } from "components/CustomLabel";
import { FC, useMemo, useState } from "react";
import { CustomLabel, CustomLabelType } from "types/CustomLabel";
import { getDefaultCustomLabels } from "helpers/customLabels";
import { CustomLabelEditModal } from "modals/CustomLabelEditModal";

interface CustomLabelsProps {
  organizationId?: string;
  isClinical: boolean | undefined;
  customLabels: CustomLabel[];
  onAddCustomLabel: () => void;
  displayHiddenButton?: boolean;
  displayEditButton?: boolean;
}

export const CustomLabels: FC<CustomLabelsProps> = ({
  organizationId,
  isClinical,
  customLabels,
  onAddCustomLabel,
  displayHiddenButton = false,
  displayEditButton = true,
}) => {
  const [displayCustomLabels, setDisplayCustomLabels] = useState(
    false || !displayHiddenButton,
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTypeKey, setEditTypeKey] = useState<CustomLabelType | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [editList, setEditList] = useState<CustomLabel[]>([]);

  const targetAchievementTimeCustomLabelsEPA = useMemo(() => {
    return customLabels.filter(
      (customLabel) =>
        customLabel.type === CustomLabelType.TARGET_ACHIEVEMENT_TIME_EPA,
    );
  }, [customLabels]);

  const targetAchievementTimeCustomLabelsCompetency = useMemo(() => {
    return customLabels.filter(
      (customLabel) =>
        customLabel.type === CustomLabelType.TARGET_ACHIEVEMENT_TIME_COMPETENCY,
    );
  }, [customLabels]);

  const skillLevelScoreCustomLabelsEPA = useMemo(() => {
    return customLabels.filter(
      (customLabel) =>
        customLabel.type === CustomLabelType.SKILL_LEVEL_SCORE_EPA,
    );
  }, [customLabels]);

  const skillLevelScoreCustomLabelsCompetency = useMemo(() => {
    return customLabels.filter(
      (customLabel) =>
        customLabel.type === CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY,
    );
  }, [customLabels]);

  const openEditor = (
    typeKey: CustomLabelType,
    list: CustomLabel[],
    fallback: CustomLabel[],
    title: string,
  ) => {
    setEditTypeKey(typeKey);
    setEditList(list.length > 0 ? list : fallback);
    setEditTitle(title);
    setEditModalOpen(true);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mt={2}>
        <Typography variant="h6" fontWeight={400}>
          Custom Labels
        </Typography>

        {displayHiddenButton && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDisplayCustomLabels(!displayCustomLabels)}
            startIcon={displayCustomLabels ? <ExpandLess /> : <ExpandMore />}
          >
            {displayCustomLabels
              ? "Hide Custom Labels"
              : "Display Custom Labels"}
          </Button>
        )}
      </Stack>
      {displayCustomLabels && (
        <Box>
          <Box mt={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Target Achievement Time - EPA
              </Typography>
              {displayEditButton && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() =>
                    openEditor(
                      CustomLabelType.TARGET_ACHIEVEMENT_TIME_EPA,
                      targetAchievementTimeCustomLabelsEPA,
                      getDefaultCustomLabels(
                        CustomLabelType.TARGET_ACHIEVEMENT_TIME_EPA,
                        isClinical,
                      ),
                      "Target Achievement Time - EPA",
                    )
                  }
                >
                  Edit
                </Button>
              )}
            </Stack>
            <CustomLabelList
              customLabels={
                targetAchievementTimeCustomLabelsEPA.length > 0
                  ? targetAchievementTimeCustomLabelsEPA
                  : getDefaultCustomLabels(
                      CustomLabelType.TARGET_ACHIEVEMENT_TIME_EPA,
                      isClinical,
                    )
              }
            />
          </Box>
          <Box mt={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Target Achievement Time - Competency
              </Typography>
              {displayEditButton && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() =>
                    openEditor(
                      CustomLabelType.TARGET_ACHIEVEMENT_TIME_COMPETENCY,
                      targetAchievementTimeCustomLabelsCompetency,
                      getDefaultCustomLabels(
                        CustomLabelType.TARGET_ACHIEVEMENT_TIME_COMPETENCY,
                        isClinical,
                      ),
                      "Target Achievement Time - Competency",
                    )
                  }
                >
                  Edit
                </Button>
              )}
            </Stack>
            <CustomLabelList
              customLabels={
                targetAchievementTimeCustomLabelsCompetency.length > 0
                  ? targetAchievementTimeCustomLabelsCompetency
                  : getDefaultCustomLabels(
                      CustomLabelType.TARGET_ACHIEVEMENT_TIME_COMPETENCY,
                      isClinical,
                    )
              }
            />
          </Box>
          <Box mt={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Skill Level Score - EPA
              </Typography>
              {displayEditButton && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() =>
                    openEditor(
                      CustomLabelType.SKILL_LEVEL_SCORE_EPA,
                      skillLevelScoreCustomLabelsEPA,
                      getDefaultCustomLabels(
                        CustomLabelType.SKILL_LEVEL_SCORE_EPA,
                      ),
                      "Skill Level Score - EPA",
                    )
                  }
                >
                  Edit
                </Button>
              )}
            </Stack>
            <CustomLabelList
              customLabels={
                skillLevelScoreCustomLabelsEPA.length > 0
                  ? skillLevelScoreCustomLabelsEPA
                  : getDefaultCustomLabels(
                      CustomLabelType.SKILL_LEVEL_SCORE_EPA,
                    )
              }
            />
          </Box>
          <Box mt={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Skill Level Score - Competency
              </Typography>
              {displayEditButton && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() =>
                    openEditor(
                      CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY,
                      skillLevelScoreCustomLabelsCompetency,
                      getDefaultCustomLabels(
                        CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY,
                      ),
                      "Skill Level Score - Competency",
                    )
                  }
                >
                  Edit
                </Button>
              )}
            </Stack>
            <CustomLabelList
              customLabels={
                skillLevelScoreCustomLabelsCompetency.length > 0
                  ? skillLevelScoreCustomLabelsCompetency
                  : getDefaultCustomLabels(
                      CustomLabelType.SKILL_LEVEL_SCORE_COMPETENCY,
                    )
              }
            />
          </Box>
        </Box>
      )}

      <CustomLabelEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        typeKey={editTypeKey || CustomLabelType.TARGET_ACHIEVEMENT_TIME_EPA}
        title={editTitle}
        customLabels={editList}
        organizationId={organizationId}
        onUpdated={onAddCustomLabel}
      />
    </Box>
  );
};
